import React, { useContext } from 'react'
import { MainConfig } from '../chapito-static'
import { StyleSheet } from '../stylesheet'
import { Title } from '../components/title'
import { environment } from '../environment'
import type { StaticSponsor, StaticSponsorCategory } from '../types'

// Sponsors use the ORIGINAL image (no _square) to preserve logo aspect
// ratios. objectFit: contain + the square container handle the display.
const SponsorImage: React.FC<{ sponsor: StaticSponsor; style?: React.CSSProperties }> = ({ sponsor, style }) => {
  if (!sponsor.image || typeof sponsor.image !== 'string') return null

  const imageId = sponsor.image as string
  const isSanitized = imageId.startsWith('z')
  let imageUrl: string
  if (environment.isBackoffice) {
    const mongoId = isSanitized ? imageId.substring(1).split('.')[0] : imageId
    imageUrl = `/api/files/${mongoId}`
  } else {
    const dotIdx = imageId.lastIndexOf('.')
    const baseId = dotIdx > 0 ? imageId.substring(0, dotIdx) : imageId
    const ext = dotIdx > 0 ? imageId.substring(dotIdx) : ''
    imageUrl = `https://${environment.staticSiteUrl}/images/${baseId}${ext}`
  }

  return (
    <a href={sponsor.url} target="_blank" rel="noopener noreferrer" style={{ ...styles.sponsorLink, ...style }} title={sponsor.name}>
      <div style={styles.sponsorImageContainer}>
        <img src={imageUrl} alt={sponsor.name} style={styles.sponsorImage} />
      </div>
    </a>
  )
}

export const PartenairesList: React.FC<{
  sponsors: StaticSponsor[]
  sponsorCategories: StaticSponsorCategory[]
  cardsPerRow?: number
  centerIcons?: boolean
}> = ({ sponsors, sponsorCategories, cardsPerRow, centerIcons }) => {
  const config = useContext(MainConfig)
  const sortedCategories = [...sponsorCategories].sort((a, b) => b.weight - a.weight)

  return (
    <div style={styles.container}>
      {sortedCategories.map((category) => {
        const categorySponsors = sponsors.filter((s) => s.categoryId === category._id).sort((a, b) => b.weight - a.weight)
        if (categorySponsors.length === 0) return null

        return (
          <div key={category._id} style={styles.categorySection}>
            <Title level={3} style={{ color: config.colorTitle, padding: '10px 0', textAlign: 'left' }}>
              {category.name}
            </Title>
            <div
              style={
                centerIcons
                  ? { ...styles.sponsorsFlexGrid, justifyContent: 'center' }
                  : { ...styles.sponsorsGrid, gridTemplateColumns: `repeat(${cardsPerRow ?? 3}, 1fr)` }
              }
            >
              {categorySponsors.map((sponsor, i) => (
                <SponsorImage key={i} sponsor={sponsor} style={centerIcons ? { width: `calc(${100 / (cardsPerRow ?? 3)}% - 12px)` } : undefined} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '16px 0',
  },
  categorySection: {},
  sponsorsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  sponsorsFlexGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  sponsorLink: {
    display: 'block',
    textDecoration: 'none',
  },
  sponsorImageContainer: {
    aspectRatio: '1/1',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #e0e0e0',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  sponsorImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
})
